import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import SEOHead from "@/components/SEOHead";

const About = () => {
  return (
    <>
      <SEOHead
        title="About Ieshia Danielle: Minister & Stewardship Coach in Gulfport, MS"
        description="Raised in the church, Ieshia Danielle helps women stop disappearing inside the lives they were called to steward. Her story, her calling, and 830+ podcast conversations."
        canonical="https://iamieshiadanielle.com/about"
        ogImage="https://vibe.filesafe.space/1787853701390843161/attachments/85108240-d531-4588-a52a-6b5fe2f30d03.jpg"
        schemaJson={[
          {
            "@type": ["WebPage", "AboutPage"],
            "@id": "https://iamieshiadanielle.com/about#webpage",
            url: "https://iamieshiadanielle.com/about",
            name: "About Ieshia Danielle: Minister & Stewardship Coach in Gulfport, MS",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            mainEntity: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "About",
                item: "https://iamieshiadanielle.com/about",
              },
            ],
          },
        ]}
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Hero */}
          <section className="bg-[#FBF1F4] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  About Ieshia
                </p>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-[#380C1E] leading-tight">
                  I help women stop disappearing inside the lives they were
                  called to steward.
                </h1>
                <p className="text-lg text-[#380C1E]/80 font-light leading-relaxed max-w-2xl">
                  I do not teach this work from a distance. I teach it because I
                  have lived the pattern of holding everything together while
                  quietly losing myself.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="#my-story"
                    className="bg-[#4A0E26] hover:bg-[#330A1F] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Read my story →
                  </a>
                  <a
                    href="https://members.iamieshiadanielle.com/communities/groups/the-becoming-community/home"
                    className="bg-[#9E1B50] hover:bg-[#801440] text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Join The Free Community →
                  </a>
                  <a
                    href="https://quiz.iamieshiadanielle.com/quiz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-transparent border border-[#9E1B50] text-[#9E1B50] hover:bg-[#9E1B50] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full inline-block transition-all"
                  >
                    Discover The Root of Your Burnout →
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 shadow-md border border-[#E8DEC8] aspect-[4/6]">
                  <img
                    src="https://vibe.filesafe.space/1787853701390843161/attachments/85108240-d531-4588-a52a-6b5fe2f30d03.jpg"
                    alt="Ieshia Danielle, minister and stewardship coach"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Who She Helps */}
          <section className="bg-white py-16 md:py-20 border-y border-[#E8DEC8]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
              <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                If this is you, I see you
              </p>
              <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                You are dependable, spiritually grounded, and quietly depleted.
              </h2>
              <div className="space-y-4 text-[#380C1E]/80 text-base md:text-lg font-light max-w-3xl mx-auto leading-relaxed">
                <p>
                  You keep showing up for your children, your church, your work,
                  and the people who count on you.
                </p>
                <p className="text-[#9E1B50] font-serif text-xl md:text-2xl font-normal italic pt-2">
                  But underneath the competence, you are asking a question you
                  have barely said out loud: When did I stop feeling like me?
                </p>
              </div>
            </div>
          </section>

          {/* More About Me */}
          <section id="my-story" className="bg-[#9E1B50] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Left: Large Headline */}
              <div className="lg:col-span-5 space-y-4">
                <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/80">
                  Gulfport, Mississippi
                </p>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] text-white">
                  Raised in the church in the most literal way possible.
                </h2>
              </div>

              {/* Right: Body text card */}
              <div className="lg:col-span-7">
                <div className="bg-white/15 backdrop-blur-sm rounded-[2rem] p-8 md:p-10 space-y-5 border border-white/10">
                  <div className="space-y-4 text-white/95 font-light leading-relaxed text-base md:text-lg">
                    <p>
                      My parents pastor Empowerment Ministries Christian Center,
                      and they are still my spiritual covering today. I grew up
                      watching them serve God's people up close, the beautiful
                      parts and the parts nobody claps for.
                    </p>
                    <p>
                      Here is what might surprise you: I am an introvert. Being
                      visible is not natural for me. It is obedience. Somewhere
                      along the way I realized that the church is full of women
                      holding their breath: serving everybody, smiling on
                      Sunday, and carrying things they have never said out loud
                      to a single soul.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Personal Transformation & Pull Quote */}
          <section className="bg-white py-16 md:py-24 border-y border-[#E8DEC8]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Left: Portrait Image */}
              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-sm aspect-[4/6]">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a95dfe6648f56e7e438aea1.jpg"
                    alt="Ieshia Danielle after her own journey from burnout to becoming"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Right: Text Content */}
              <div className="lg:col-span-7 space-y-8 pt-2">
                <p className="text-xs font-semibold text-[#380C1E]/60 uppercase tracking-[0.3em]">
                  My Transformation
                </p>

                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] leading-[1.15]">
                  I looked capable on the outside. Inside, I was operating like
                  a robot.
                </h2>

                <div className="space-y-4 text-[#380C1E]/80 font-light leading-relaxed text-base md:text-lg">
                  <p>
                    Perfectionism taught me to perform. Responsibility taught me
                    to keep moving. Faith taught me to trust God, but healing
                    taught me that stewardship also meant learning to hear
                    myself again.
                  </p>
                  <p>
                    I had to name the beliefs underneath my choices, stop
                    treating exhaustion like evidence of faithfulness, and
                    rebuild self-trust one decision at a time.
                  </p>
                </div>

                {/* Divider Line */}
                <div className="w-full h-[2px] bg-[#9E1B50]" />

                {/* Pull Quote */}
                <p className="font-serif text-xl md:text-2xl font-normal italic leading-snug text-[#380C1E]/90">
                  Burnout is not a character flaw, and it is not a faith
                  failure. It has a source. A source can be found, and a found
                  source can be healed.
                </p>
              </div>
            </div>
          </section>

          {/* Podcast Banner on About */}
          <section className="bg-[#9E1B50] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left: Portrait Image */}
              <div className="lg:col-span-5">
                <div className="rounded-[2.5rem] overflow-hidden bg-neutral-900/20 border border-white/10 shadow-lg aspect-[4/6]">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a908c5f722007d7fff61e4c.jpg"
                    alt="Ieshia Danielle speaking at the Style Evolved event"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Right: Text Content */}
              <div className="lg:col-span-7 space-y-6 pt-2">
                <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/80">
                  after I got clear on who I am
                </p>

                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] text-white">
                  Ideas Met Execution
                </h2>

                <p className="text-white/90 leading-relaxed text-base md:text-lg font-light max-w-xl">
                  In 2023 I started Between Me &amp; You because people of faith
                  deserved a room where the truth could get told. We talked
                  about infidelity, grief, addiction, lost identity, and the God
                  who found us there.
                </p>

                <div className="pt-2 flex flex-wrap gap-4">
                  <Link
                    to="/podcast"
                    className="bg-white hover:bg-white/90 text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full inline-block transition-all shadow-sm"
                  >
                    Enter the conversation →
                  </Link>
                  <a
                    href="https://members.iamieshiadanielle.com/communities/groups/the-becoming-community/home"
                    className="bg-transparent border border-white text-white hover:bg-white hover:text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full inline-block transition-all"
                  >
                    Join The Free Community →
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Events and Impact */}
          <section className="bg-white py-16 md:py-24 border-y border-[#E8DEC8]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-4">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  Real rooms. Real impact.
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  The message became a movement.
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Event 1 */}
                <div className="bg-[#EFE6D2] rounded-3xl border border-[#E8DEC8] p-8 space-y-4 shadow-sm">
                  <span className="bg-white text-[#9E1B50] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-block">
                    100+ Women who needed the night
                  </span>
                  <h3 className="font-serif text-2xl font-normal text-[#380C1E]">
                    More Than a Mother
                  </h3>
                  <p className="text-[#380C1E]/70 text-sm leading-relaxed font-light">
                    A two-night experience created for moms who were exhausted,
                    overwhelmed, and ready to remember the woman beyond the
                    role.
                  </p>
                </div>

                {/* Event 2 */}
                <div className="bg-[#EFE6D2] rounded-3xl border border-[#E8DEC8] p-8 space-y-4 shadow-sm">
                  <span className="bg-white text-[#9E1B50] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-block">
                    Guided Reset Experience
                  </span>
                  <h3 className="font-serif text-2xl font-normal text-[#380C1E]">
                    Identity Reset
                  </h3>
                  <p className="text-[#380C1E]/70 text-sm leading-relaxed font-light">
                    A guided experience helping women identify the labels and
                    beliefs shaping their choices, then replace them with truth
                    and practical action.
                  </p>
                  <div className="pt-2">
                    <Link
                      to="/shop"
                      className="text-[#9E1B50] font-bold uppercase text-[11px] tracking-wider hover:underline"
                    >
                      Explore Identity Reset →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Final Call to Action */}
          <section className="bg-[#9E1B50] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-white/80">
                Your next step
              </p>
              <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl font-normal leading-[1.15] text-white max-w-4xl">
                You do not need another thing to carry. You need a path.
              </h2>
              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/programs/becoming-intensive"
                  className="bg-white hover:bg-white/90 text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full shadow-sm inline-block transition-all"
                >
                  Explore the pathway →
                </Link>
                <Link
                  to="/book"
                  className="bg-transparent border border-white text-white hover:bg-white hover:text-[#9E1B50] font-bold uppercase tracking-wider text-[11px] px-8 py-4 rounded-full transition-all"
                >
                  Book Ieshia →
                </Link>
              </div>
            </div>
          </section>

          <PrayerForm />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;
